import { FileDoneOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { CloseBillFiscalInvoiceIn } from "../../../../../api/bills";

interface FiscalInvoiceBuyerStripProps {
  fiscalInvoice: CloseBillFiscalInvoiceIn;
  /** Касата кара картов терминал — „С карта" и „Смесено" ги няма; лентата
   *  казва защо (същият текст като при купувача). */
  cardTerminal?: boolean;
  /** Тесен екран — по-дребни кегели и отстъпи. */
  compact: boolean;
  /** Хоризонталният падинг го дава РОДИТЕЛЯТ — подравнен с хедъра на модала. */
  className?: string;
}

/**
 * „Закрий" с фискална фактура — лента на цяла ширина под хедъра (като
 * лентата на клиента), само за четене: купувачът, въведен във „Фактуриране",
 * и че номерът ще се даде от апарата. Смяна на купувача — „Отказ" и наново.
 */
export function FiscalInvoiceBuyerStrip({
  fiscalInvoice,
  cardTerminal = false,
  compact,
  className = "",
}: FiscalInvoiceBuyerStripProps) {
  const { t } = useTranslation();
  const { customer } = fiscalInvoice;
  const identifiers = [
    t("fiscalInvoice.close.eik", { eik: customer.eik }),
    customer.vat_number ? t("fiscalInvoice.close.vat", { vat: customer.vat_number }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`flex items-center border-b border-pos-line2 bg-pos-marked text-pos-ink ${
        compact ? "gap-2.5 py-1.5" : "gap-3 py-2"
      } ${className}`}
    >
      <span
        className={`inline-flex shrink-0 items-center justify-center rounded-lg border border-pos-line bg-pos-card ${
          compact ? "size-6 text-[12px]" : "size-7 text-sm"
        }`}
      >
        {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
        <FileDoneOutlined className="!text-pos-accent-ink" />
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={`truncate font-extrabold leading-[1.2] ${compact ? "text-[13px]" : "text-[15px]"}`}
        >
          {t("fiscalInvoice.close.buyer", { name: customer.company_name })}
        </div>
        <div
          // Пренася се, не се реже — „номерът от апарата" трябва да се чете
          className={`font-medium leading-[1.3] text-pos-soft ${
            compact ? "text-[11px]" : "text-xs"
          }`}
        >
          <span className="font-pos-mono">{identifiers}</span>
          {" · "}
          {t("fiscalInvoice.close.numberFromDevice")}
        </div>
        {cardTerminal && (
          <div
            // Цветът на предупреждението при купувача (`NoticeBanner` „warn")
            className={`mt-0.5 font-semibold leading-[1.3] text-[var(--color-warn-ink)] ${
              compact ? "text-[11px]" : "text-xs"
            }`}
          >
            {t("fiscalInvoice.buyer.cardTerminal")}
          </div>
        )}
      </div>
    </div>
  );
}
